import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import {
  AnnotationIcon,
  CalendarIcon,
  ClipboardListIcon,
  CogIcon,
  DocumentTextIcon,
  HomeIcon,
  MapIcon,
  PhotographIcon,
} from "@heroicons/react/outline";

import Breadcrumbs from "../../components/Shared/Breadcrumbs";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import SocialPostCalendar from "../../components/SocialPosts/SocialPostCalendar";
import SocialSettings from "../../components/SocialPosts/SocialSettings";
import useAgencies from "../../data/useAgencies";
import useMeta from "../../data/useMeta";
import classNames from "../../utils/classNames";
import ScheduleManager from "../../components/SocialPosts/ScheduleManager.js";
import { SingleOneOffPost } from "../../components/SocialPosts/SingleOneOffPost.tsx";


export default function SocialPostManager() {
  const { agencyId, pageId, section } = useParams();
  // const { socialPosts } = useSocialPosts(agencyId);
  const { agencies } = useAgencies();
  const { metaPageConfigs } = useMeta(agencyId);
  const [viewType, setViewType] = useState(0);
  const [agency, setAgency] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAgency = useCallback(() => {
    if (
      !agencies.isLoading &&
      agencies.data.length > 0 &&
      !metaPageConfigs.isLoading &&
      metaPageConfigs.data
    ) {
      let agency = agencies.data.find((a) => a.id === agencyId);

      setCurrentPage(metaPageConfigs.data.data?.find((p) => p.id === pageId));
      setAgency(agency);
      setLoading(false);
    }
  }, [
    agencies.isLoading,
    agencies.data,
    metaPageConfigs.isLoading,
    metaPageConfigs.data,
    agencyId,
  ]);

  useEffect(() => {
    setLoading(true);
    loadAgency();
  }, [agencies, agencyId, loadAgency]);

  const getPanel = (agency) => {
    switch (section) {
      case "social-settings":
        return <SocialSettings agency={agency} />;
      case "evergreens":
        return (
          <ScheduleManager agency={agency} triggerTypeName={"Evergreen"} />
        );
      case "infographics":
        return (
          <ScheduleManager agency={agency} triggerTypeName={"Infographic"} />
        );
      case "properties":
        return (
          <ScheduleManager agency={agency} triggerTypeName={"Properties"} />
        );
      case "reviews":
        return <ScheduleManager agency={agency} triggerTypeName={"Reviews"} />;
      case "news":
        return <ScheduleManager agency={agency} triggerTypeName={"News"} />;
      case "single-post":
        return <SingleOneOffPost />;
      case "":
      default:
        return <SocialPostCalendar agency={agency} />;
    }
  };

  const subNavigation = () => {
    return [
      { icon: CalendarIcon, name: "Calendar", href: "" },
      { icon: MapIcon, name: "Evergreens", href: "evergreens" },
      { icon: PhotographIcon, name: "Infographics", href: "infographics" },
      { icon: HomeIcon, name: "Properties", href: "properties" },
      { icon: AnnotationIcon, name: "Reviews", href: "reviews" },
      { icon: DocumentTextIcon, name: "News", href: "news" },
      { icon: ClipboardListIcon, name: "Single Post", href: "single-post" },
      { icon: CogIcon, name: "Settings", href: "social-settings" },
    ];
  };

  const pages = [
    { to: "/admin/agencies", label: "Agencies", current: false },
    {
      to: `/admin/agencies/${agencyId}`,
      label: agencyId && agency ? `${agency.name}` : "",
      current: false,
    },
    { to: "..", label: "Pages", current: false },
    { to: "", label: currentPage ? currentPage.pageName : "", current: true },
  ];

  // console.log("rendering 2");

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="h-80 max-w-7xl flex items-center justify-center">
          <LoadingWheel width="w-10" height="h-10" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <Breadcrumbs pages={pages} />
        <div className="sm:flex-auto my-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {currentPage && `${currentPage.pageName}`}
          </h3>
          <p className="mt-2 text-sm text-gray-700">
            {currentPage &&
              `View and edit ${currentPage.pageName}'s social post schedules and content`}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="min-h-[256px] divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
            <aside className="py-6 lg:col-span-2">
              <nav className="space-y-1">
                {subNavigation().map((item) => {
                  let currentSection = section ?? "";
                  let current = item.href === currentSection;
                  return (
                    <Link
                      key={item.name}
                      to={item.href ?? ""}
                      className={classNames(
                        current
                          ? "bg-etpink-50 border-etpink-500 text-etpink-700 hover:bg-etpink-50 hover:text-etpink-700"
                          : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                        "group border-l-4 px-3 py-2 flex items-center text-sm font-medium"
                      )}
                      aria-current={current ? "page" : undefined}
                    >
                      <item.icon
                        className={classNames(
                          current
                            ? "text-etpink-500 group-hover:text-etpink-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "flex-shrink-0 -ml-1 mr-3 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>
            <div className="bg-gray-50 lg:col-span-10">
              <div className="h-full py-6 px-4 sm:p-6 lg:pb-8">
                {getPanel(agency)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
